import { ItListItem } from 'types/graphql'

import { useFormContext } from '@redwoodjs/forms'

import { toast } from 'src/components/widgets/Toaster/Toaster'
import upload from 'src/lib/assets/upload'
import useField from 'src/lib/hooks/form/useField'

import useDeleteItListItemFile from '../hooks/useDeleteItListItemFile'

/*
 * Uploader hook for document items
 */
export default function useUploader() {
  const { getValues } = useFormContext<ItListItem>()

  const assetType = 'itListItemDocumentBinary'
  const {
    error,
    fieldState,
    onChange: onFieldUpdate,
    value,
  } = useField('document.documentUrl')

  const fileNameField = useField<string>('document.fileName')

  const [status, setStatus] = React.useState<
    'Required' | 'Saving...' | 'Saved'
  >('Required')

  React.useEffect(() => {
    setStatus(value ? 'Saved' : 'Required')
  }, [value])

  const [onDelete] = useDeleteItListItemFile({
    async onCompleted() {
      fileNameField.onChange(undefined)
      onFieldUpdate('')

      toast('success', 'Document removed')
    },
  })

  const onChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files.length > 0) {
      setStatus('Saving...')

      const file = ev.target.files[0]

      if (!isAccepted(file.type, ['application/pdf'])) {
        toast('error', 'Invalid file type')
        setStatus('Required')
        return
      }

      const toastID = 'ReviewUpload'

      toast('loading', 'Uploading...', undefined, {
        id: toastID,
        duration: Infinity,
      })

      try {
        const uploadData = await upload(assetType, getValues('id'), file)

        onFieldUpdate(uploadData.url)
        fileNameField.onChange(file.name)

        toast('success', 'Done', undefined, { id: toastID, duration: 2000 })
      } catch (err: unknown) {
        console.error(err)
        toast('error', 'Oops, uploading failed', undefined, {
          id: toastID,
          duration: 2000,
        })

        ev.target.value = ''
      }

      setStatus('Saved')
    }
    ev.target.value = ''
  }

  return {
    error,
    fileName: fileNameField.value,
    isDirty: !!value,
    status,
    value,
    fieldState,
    onChange,
    async onDelete() {
      await onDelete({
        variables: {
          id: getValues('id'),
          type: assetType,
        },
      })
    },
  }
}

/**
 * Tests a given file type to an array of accepted file types.
 * A default value for accepted types is provided as [audio, image, video].
 * @param fileType
 * @param accepts
 * @returns boolean
 */
export const isAccepted = (
  fileType: string,
  accepts: Array<string> = ['audio', 'image', 'video']
): boolean => accepts.some((type) => fileType.includes(type))
